import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/lib/auth/store';
import { findAclRule } from '@/lib/permissions/acl-route-rules';
import { hasPermission, usePermissionStore } from '@/lib/permissions/store';

/**
 * Page-level ACL block. Wraps the routed page inside AppShell, so a URL typed
 * by hand is blocked the same way the sidebar hides it.
 * Sub-superadmin → own ACL. Reseller / user under a sub-superadmin → that
 * sub-superadmin's ACL (only for modules the sidebar also gates). Others → open.
 */
export function AclRouteGuard({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const isSub = useAuthStore((s) => s.isSubSuperAdmin());
  const perms = usePermissionStore((s) => s.perms);
  const inherited = usePermissionStore((s) => s.inheritedPerms);

  const rule = findAclRule(pathname);
  if (!rule) return <>{children}</>;

  let allowed = true;
  if (isSub) {
    allowed = hasPermission(perms, rule.path, rule.action);
  } else if (rule.inherited && inherited) {
    allowed = Boolean(inherited[rule.inherited]?.view_permission);
  }
  return allowed ? <>{children}</> : <Navigate to="/" replace />;
}
